import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'

const springConfig = { type: "spring", stiffness: 60, damping: 10 }

interface Client {
  id: string
  name: string
  email: string
  phone?: string
}

interface Payment {
  id: string
  service_type: string
  service_title: string
  amount: number
  status: string
  created_at: string
  receipt_url?: string
  metadata?: {
    sessionsPurchased?: number
    sessionsRemaining?: number
  }
}

interface Booking {
  id: string
  service_type: string
  service_title: string
  status: string
  scheduled_start: string
  scheduled_end: string
  meeting_url?: string
}

export default function ClientPortal() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [client, setClient] = useState<Client | null>(null)
  const [payments, setPayments] = useState<Payment[]>([])
  const [bookings, setBookings] = useState<Booking[]>([])
  const [activeTab, setActiveTab] = useState<'overview' | 'bookings' | 'payments'>('overview')
  const [calendlyUrl, setCalendlyUrl] = useState<string | null>(null)
  const [bookingLoading, setBookingLoading] = useState(false)

  const accessToken = searchParams.get('token')
  const email = searchParams.get('email')
  const [authError, setAuthError] = useState<string | null>(null)
  const [emailInput, setEmailInput] = useState('')
  const [requestingAccess, setRequestingAccess] = useState(false)

  useEffect(() => {
    if (accessToken && email) {
      verifyToken()
    } else if (email || user) {
      loadClientData()
    } else {
      // No authentication provided, show access form
      setLoading(false)
    }
  }, [user, accessToken, email])

  const verifyToken = async () => {
    try {
      const API_URL = import.meta.env.VITE_API_URL || '/api'
      const response = await fetch(`${API_URL}/generate-portal-token?token=${accessToken}&email=${encodeURIComponent(email || '')}`)
      
      if (response.ok) {
        const data = await response.json()
        if (data.success && data.client) {
          // Token is valid and client data is returned - use it directly
          setClient(data.client)
          // Use payments and bookings from API response (bypasses RLS)
          if (data.payments) {
            setPayments(data.payments)
          }
          if (data.bookings) {
            setBookings(data.bookings)
          }
          setLoading(false)
        } else if (data.success) {
          // Token is valid but no client data - try loading via email
          loadClientData()
        } else {
          setAuthError(data.error || 'Invalid or expired access link')
          setLoading(false)
        }
      } else {
        const errorData = await response.json().catch(() => ({}))
        setAuthError(errorData.error || 'Unable to verify access link')
        setLoading(false)
      }
    } catch (error) {
      console.error('Error verifying token:', error)
      setAuthError('Unable to verify access link')
      setLoading(false)
    }
  }

  const loadClientData = async () => {
    try {
      setLoading(true)
      setAuthError(null)

      // If user is authenticated, use their user ID
      // Otherwise, try to find client by email from token or email param
      let clientQuery = supabase.from('clients').select('*')

      if (user) {
        clientQuery = clientQuery.eq('user_id', user.id)
      } else if (email) {
        clientQuery = clientQuery.eq('email', email)
      } else {
        setLoading(false)
        setAuthError('Please provide an email or access token')
        return
      }

      const { data: clients, error: clientError } = await clientQuery

      if (clientError || !clients || clients.length === 0) {
        console.error('Client not found:', clientError)
        
        // If email was provided, try to request access via API
        if (email) {
          try {
            const API_URL = import.meta.env.VITE_API_URL || '/api'
            const tokenResponse = await fetch(`${API_URL}/generate-portal-token`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ email: email }),
            })
            
            if (tokenResponse.ok) {
              const tokenData = await tokenResponse.json()
              if (tokenData.token) {
                // Redirect with token
                navigate(`/portal?token=${tokenData.token}&email=${encodeURIComponent(email)}`, { replace: true })
                return
              }
            }
          } catch (apiError) {
            console.error('Error requesting access:', apiError)
          }
        }
        
        setAuthError('Client account not found. If you just made a payment, please wait a moment and try again. Otherwise, please contact support.')
        setLoading(false)
        return
      }

      const clientData = clients[0]
      setClient(clientData)

      // Load payments
      const { data: paymentsData, error: paymentsError } = await supabase
        .from('payments')
        .select('*')
        .eq('client_id', clientData.id)
        .order('created_at', { ascending: false })

      if (!paymentsError && paymentsData) {
        setPayments(
          paymentsData.map((p) => ({
            id: p.id,
            service_type: p.service_type,
            service_title: p.service_title || `${p.service_type} Service`,
            amount: p.amount / 100, // Convert from cents
            status: p.status,
            created_at: p.created_at,
            receipt_url: p.receipt_url || undefined,
            metadata: p.metadata || {},
          }))
        )
      }

      // Load bookings
      const { data: bookingsData, error: bookingsError } = await supabase
        .from('bookings')
        .select('*')
        .eq('client_id', clientData.id)
        .order('scheduled_start', { ascending: false })

      if (!bookingsError && bookingsData) {
        setBookings(
          bookingsData.map((b) => ({
            id: b.id,
            service_type: b.service_type,
            service_title: b.service_title || `${b.service_type} Service`,
            status: b.status,
            scheduled_start: b.scheduled_start,
            scheduled_end: b.scheduled_end,
            meeting_url: b.meeting_url || undefined,
          }))
        )
      }

      setLoading(false)
    } catch (error) {
      console.error('Error loading client data:', error)
      setLoading(false)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
      case 'confirmed':
        return 'text-green-600 dark:text-green-400'
      case 'pending':
      case 'scheduled':
        return 'text-yellow-600 dark:text-yellow-400'
      case 'failed':
      case 'canceled':
        return 'text-red-600 dark:text-red-400'
      default:
        return 'text-text-primary/70'
    }
  }

  if (loading) {
    return (
      <section className="relative py-20 sm:py-28 px-4 sm:px-6 lg:px-8 min-h-screen overflow-hidden bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="text-4xl mb-4">⏳</div>
          <p className="text-text-primary/70">Loading your portal...</p>
        </div>
      </section>
    )
  }

  const handleRequestAccess = async () => {
    if (!emailInput.trim()) {
      setAuthError('Please enter your email address')
      return
    }

    setRequestingAccess(true)
    setAuthError(null)
    
    try {
      const API_URL = import.meta.env.VITE_API_URL || '/api'
      const response = await fetch(`${API_URL}/generate-portal-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: emailInput.trim() }),
      })
      
      if (response.ok) {
        const data = await response.json()
        if (data.success) {
          if (data.warning) {
            // Email failed but token was generated - show warning with portal link
            const message = `${data.warning}\n\nAccess Link: ${data.portalUrl}\n\nYou can copy this link to access your portal.`
            alert(message)
          } else {
            alert('Check your email for an access link! We\'ve sent you a secure link to access your client portal.')
          }
          setEmailInput('')
        } else {
          setAuthError('Unable to send access link. Please contact support.')
        }
      } else {
        // Try to parse error response
        let errorMessage = 'Unable to send access link. Please try again or contact support.'
        try {
          const errorData = await response.json()
          errorMessage = errorData.error || errorMessage
        } catch (parseError) {
          // If response is not JSON, use status text
          if (response.status === 404) {
            errorMessage = 'No account found with this email address. Please check your email or contact support.'
          } else if (response.status === 500) {
            errorMessage = 'Server error. Please try again later or contact support.'
          } else if (response.status === 0 || response.status >= 500) {
            errorMessage = 'Unable to connect to server. Please check your internet connection and try again.'
          }
        }
        setAuthError(errorMessage)
      }
    } catch (error) {
      console.error('Error requesting access:', error)
      // Network errors or fetch failures
      if (error instanceof TypeError && error.message.includes('fetch')) {
        setAuthError('Unable to connect to server. Please check your internet connection and try again.')
      } else {
        setAuthError('Unable to send access link. Please try again or contact support.')
      }
    } finally {
      setRequestingAccess(false)
    }
  }

  if (!client && !loading) {
    return (
      <section className="relative py-20 sm:py-28 px-4 sm:px-6 lg:px-8 min-h-screen overflow-hidden bg-background flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="neu p-8 rounded-2xl max-w-md w-full"
        >
          <div className="text-center mb-6">
            <div className="text-4xl mb-4">🔒</div>
            <h2 className="text-2xl font-semibold mb-2 text-text-primary">Access Your Client Portal</h2>
            <p className="text-text-primary/70 mb-2">
              {authError || 'Enter your email to receive a secure access link'}
            </p>
            {!authError && (
              <p className="text-sm text-text-primary/50">
                No password needed! We'll send you a secure link that expires in 30 days.
              </p>
            )}
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault()
              handleRequestAccess()
            }}
            className="space-y-4"
          >
            <div>
              <label htmlFor="email" className="block text-sm font-medium mb-2 text-text-primary/70">
                Email Address
              </label>
              <input
                id="email"
                type="email"
                value={emailInput}
                onChange={(e) => {
                  setEmailInput(e.target.value)
                  setAuthError(null)
                }}
                placeholder="your@email.com"
                required
                className="w-full px-4 py-3 neu rounded-xl text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent/30 transition-all"
              />
            </div>

            {authError && (
              <div className="p-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-500/50 text-red-600 dark:text-red-400 text-sm">
                {authError}
              </div>
            )}

            <motion.button
              type="submit"
              disabled={requestingAccess || !emailInput.trim()}
              className="w-full px-6 py-3 neu rounded-xl font-semibold text-text-primary neu-hover disabled:opacity-50 disabled:cursor-not-allowed bg-accent/10 hover:bg-accent/20 transition-colors"
              whileHover={requestingAccess ? {} : { scale: 1.02, y: -2 }}
              whileTap={requestingAccess ? {} : { scale: 0.98 }}
              transition={springConfig}
            >
              {requestingAccess ? 'Sending...' : 'Send Access Link'}
            </motion.button>
          </form>

          <div className="mt-6 pt-6 border-t border-shadow-dark/10">
            <p className="text-sm text-text-primary/60 text-center mb-2">
              Need help accessing your portal?
            </p>
            <p className="text-xs text-text-primary/50 text-center mb-4">
              If you've made a payment, enter the email address you used. You can request a new access link anytime.
            </p>
          </div>

          <motion.button
            onClick={() => navigate('/')}
            className="w-full mt-4 px-6 py-3 neu rounded-xl font-medium text-text-primary/70 hover:text-text-primary transition-colors"
            whileHover={{ scale: 1.02, y: -2 }}
            whileTap={{ scale: 0.98 }}
            transition={springConfig}
          >
            Back to Home
          </motion.button>
        </motion.div>
      </section>
    )
  }

  if (authError && !client) {
    return (
      <section className="relative py-20 sm:py-28 px-4 sm:px-6 lg:px-8 min-h-screen overflow-hidden bg-background flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="neu p-8 rounded-2xl max-w-md w-full text-center"
        >
          <div className="text-4xl mb-4">🔒</div>
          <h2 className="text-2xl font-semibold mb-4 text-text-primary">Access Denied</h2>
          <p className="text-text-primary/70 mb-6">
            {authError}
          </p>
          {email && (
            <motion.button
              onClick={handleRequestAccess}
              disabled={requestingAccess}
              className="px-6 py-3 neu rounded-xl font-semibold text-text-primary neu-hover mb-4 disabled:opacity-50"
              whileHover={{ scale: 1.02, y: -2 }}
              whileTap={{ scale: 0.98 }}
              transition={springConfig}
            >
              {requestingAccess ? 'Sending...' : 'Request New Access Link'}
            </motion.button>
          )}
          <motion.button
            onClick={() => navigate('/')}
            className="px-6 py-3 neu rounded-xl font-semibold text-text-primary neu-hover"
            whileHover={{ scale: 1.02, y: -2 }}
            whileTap={{ scale: 0.98 }}
            transition={springConfig}
          >
            Back to Home
          </motion.button>
        </motion.div>
      </section>
    )
  }

  const upcomingBookings = bookings.filter(
    (b) => b.status === 'scheduled' || b.status === 'confirmed'
  )
  const pastBookings = bookings.filter((b) => b.status === 'completed')

  // Calculate total available sessions from all completed payments
  const totalSessionsRemaining = payments
    .filter((p) => p.status === 'completed')
    .reduce((total, payment) => {
      const sessions = payment.metadata?.sessionsRemaining || 0
      return total + sessions
    }, 0)

  // Find first payment with available sessions for booking
  const paymentWithSessions = payments.find(
    (p) => p.status === 'completed' && (p.metadata?.sessionsRemaining || 0) > 0
  )

  const handleBookSession = async () => {
    if (!paymentWithSessions || !client) {
      alert('No available sessions. Please make a payment first.')
      return
    }

    setBookingLoading(true)
    try {
      const API_URL = import.meta.env.VITE_API_URL || '/api'
      const response = await fetch(`${API_URL}/generate-booking-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          paymentId: paymentWithSessions.id,
          clientEmail: client.email,
        }),
      })

      if (!response.ok) {
        const errorData = await response.json()
        alert(errorData.error || 'Failed to generate booking link')
        setBookingLoading(false)
        return
      }

      const data = await response.json()
      if (data.success && data.bookingUrl) {
        setCalendlyUrl(data.bookingUrl)
      }
    } catch (error) {
      console.error('Error generating booking link:', error)
      alert('Failed to generate booking link. Please try again.')
    } finally {
      setBookingLoading(false)
    }
  }

  const handleCloseCalendly = () => {
    setCalendlyUrl(null)
    // Reload client data to refresh session credits after booking
    setTimeout(() => {
      if (client) {
        loadClientData()
      }
    }, 2000)
  }

  return (
    <section className="relative py-20 sm:py-28 px-4 sm:px-6 lg:px-8 min-h-screen overflow-hidden bg-background">
      <div className="container mx-auto max-w-6xl relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-4 font-display leading-tight">
            <span className="text-gradient-glow">Client</span>{' '}
            <span className="text-text-primary">Portal</span>
          </h1>
          <p className="text-lg text-text-primary/70">
            Welcome back, {client?.name || 'there'}
          </p>
        </motion.div>

        {/* Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex justify-center mb-8"
        >
          <div className="neu p-1.5 rounded-2xl inline-flex gap-2">
            {(['overview', 'bookings', 'payments'] as const).map((tab) => (
              <motion.button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-semibold text-sm sm:text-base transition-all relative capitalize ${
                  activeTab === tab
                    ? 'text-text-primary'
                    : 'text-text-primary/60 hover:text-text-primary'
                }`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                transition={springConfig}
              >
                {activeTab === tab && (
                  <motion.div
                    layoutId="activeTab"
                    className="absolute inset-0 bg-accent/10 rounded-xl"
                    transition={springConfig}
                  />
                )}
                <span className="relative z-10">{tab}</span>
              </motion.button>
            ))}
          </div>
        </motion.div>

        {/* Content */}
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Available Sessions Card */}
              <div className="neu p-6 rounded-2xl bg-accent/5">
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <h3 className="text-sm font-semibold text-text-primary/70 uppercase tracking-wide mb-2">
                      Available Sessions
                    </h3>
                    <p className="text-3xl font-bold text-text-primary">
                      {totalSessionsRemaining} {totalSessionsRemaining === 1 ? 'session' : 'sessions'}
                    </p>
                    <p className="text-sm text-text-primary/70 mt-1">
                      Each session is 1 hour long
                    </p>
                  </div>
                  {totalSessionsRemaining > 0 && (
                    <motion.button
                      onClick={handleBookSession}
                      disabled={bookingLoading}
                      className="px-6 py-3 neu rounded-xl font-semibold text-text-primary neu-hover disabled:opacity-50 disabled:cursor-not-allowed bg-accent/10 hover:bg-accent/20 transition-colors"
                      whileHover={{ scale: 1.02, y: -2 }}
                      whileTap={{ scale: 0.98 }}
                      transition={springConfig}
                    >
                      {bookingLoading ? 'Loading...' : 'Book Session'}
                    </motion.button>
                  )}
                </div>
                {totalSessionsRemaining === 0 && (
                  <p className="text-sm text-text-primary/70 mt-2">
                    No sessions available. <a href="/#contact" className="text-accent hover:underline">Contact</a> to purchase sessions.
                  </p>
                )}
              </div>

              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="neu p-6 rounded-2xl">
                  <div className="text-3xl mb-2">📅</div>
                  <div className="text-2xl font-bold text-text-primary mb-1">
                    {upcomingBookings.length}
                  </div>
                  <div className="text-sm text-text-primary/70">Upcoming Bookings</div>
                </div>
                <div className="neu p-6 rounded-2xl">
                  <div className="text-3xl mb-2">✅</div>
                  <div className="text-2xl font-bold text-text-primary mb-1">
                    {pastBookings.length}
                  </div>
                  <div className="text-sm text-text-primary/70">Completed Sessions</div>
                </div>
                <div className="neu p-6 rounded-2xl">
                  <div className="text-3xl mb-2">💳</div>
                  <div className="text-2xl font-bold text-text-primary mb-1">
                    {payments.filter((p) => p.status === 'completed').length}
                  </div>
                  <div className="text-sm text-text-primary/70">Total Payments</div>
                </div>
              </div>

              {/* Upcoming Bookings */}
              {upcomingBookings.length > 0 && (
                <div className="neu p-6 rounded-2xl">
                  <h3 className="text-xl font-semibold mb-4 text-text-primary">
                    Upcoming Bookings
                  </h3>
                  <div className="space-y-4">
                    {upcomingBookings.slice(0, 3).map((booking) => (
                      <div
                        key={booking.id}
                        className="p-4 neu rounded-xl border border-shadow-dark/10"
                      >
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <h4 className="font-semibold text-text-primary">
                              {booking.service_title}
                            </h4>
                            <p className="text-sm text-text-primary/70">
                              {formatDate(booking.scheduled_start)}
                            </p>
                          </div>
                          <span
                            className={`text-xs font-medium px-2 py-1 rounded ${getStatusColor(
                              booking.status
                            )}`}
                          >
                            {booking.status}
                          </span>
                        </div>
                        {booking.meeting_url && (
                          <a
                            href={booking.meeting_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-accent hover:underline"
                          >
                            Join Meeting →
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recent Payments */}
              {payments.length > 0 && (
                <div className="neu p-6 rounded-2xl">
                  <h3 className="text-xl font-semibold mb-4 text-text-primary">
                    Recent Payments
                  </h3>
                  <div className="space-y-4">
                    {payments.slice(0, 3).map((payment) => (
                      <div
                        key={payment.id}
                        className="p-4 neu rounded-xl border border-shadow-dark/10 flex justify-between items-center"
                      >
                        <div>
                          <h4 className="font-semibold text-text-primary">
                            {payment.service_title}
                          </h4>
                          <p className="text-sm text-text-primary/70">
                            {formatDate(payment.created_at)}
                          </p>
                        </div>
                        <div className="text-right">
                          <div className="font-semibold text-text-primary">
                            ${payment.amount.toFixed(2)}
                          </div>
                          <span
                            className={`text-xs font-medium ${getStatusColor(payment.status)}`}
                          >
                            {payment.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'bookings' && (
            <div className="space-y-6">
              {bookings.length === 0 ? (
                <div className="neu p-12 rounded-2xl text-center">
                  <div className="text-4xl mb-4">📅</div>
                  <p className="text-text-primary/70">No bookings found.</p>
                </div>
              ) : (
                <>
                  {upcomingBookings.length > 0 && (
                    <div>
                      <h3 className="text-xl font-semibold mb-4 text-text-primary">
                        Upcoming
                      </h3>
                      <div className="space-y-4">
                        {upcomingBookings.map((booking) => (
                          <div
                            key={booking.id}
                            className="neu p-6 rounded-2xl"
                          >
                            <div className="flex justify-between items-start mb-4">
                              <div>
                                <h4 className="text-lg font-semibold text-text-primary mb-2">
                                  {booking.service_title}
                                </h4>
                                <p className="text-text-primary/70">
                                  {formatDate(booking.scheduled_start)}
                                </p>
                              </div>
                              <span
                                className={`text-sm font-medium px-3 py-1 rounded ${getStatusColor(
                                  booking.status
                                )}`}
                              >
                                {booking.status}
                              </span>
                            </div>
                            {booking.meeting_url && (
                              <a
                                href={booking.meeting_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-accent hover:underline"
                              >
                                Join Meeting →
                              </a>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {pastBookings.length > 0 && (
                    <div>
                      <h3 className="text-xl font-semibold mb-4 text-text-primary">
                        Past Sessions
                      </h3>
                      <div className="space-y-4">
                        {pastBookings.map((booking) => (
                          <div
                            key={booking.id}
                            className="neu p-6 rounded-2xl opacity-75"
                          >
                            <div className="flex justify-between items-start">
                              <div>
                                <h4 className="text-lg font-semibold text-text-primary mb-2">
                                  {booking.service_title}
                                </h4>
                                <p className="text-text-primary/70">
                                  {formatDate(booking.scheduled_start)}
                                </p>
                              </div>
                              <span className="text-sm font-medium text-text-primary/70">
                                Completed
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {activeTab === 'payments' && (
            <div className="space-y-6">
              {payments.length === 0 ? (
                <div className="neu p-12 rounded-2xl text-center">
                  <div className="text-4xl mb-4">💳</div>
                  <p className="text-text-primary/70">No payments found.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {payments.map((payment) => (
                    <div key={payment.id} className="neu p-6 rounded-2xl">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h4 className="text-lg font-semibold text-text-primary mb-2">
                            {payment.service_title}
                          </h4>
                          <p className="text-sm text-text-primary/70">
                            {formatDate(payment.created_at)}
                          </p>
                        </div>
                        <div className="text-right">
                          <div className="text-xl font-bold text-text-primary mb-1">
                            ${payment.amount.toFixed(2)}
                          </div>
                          <span
                            className={`text-sm font-medium ${getStatusColor(payment.status)}`}
                          >
                            {payment.status}
                          </span>
                        </div>
                      </div>
                      {payment.receipt_url && (
                        <a
                          href={payment.receipt_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-accent hover:underline text-sm"
                        >
                          View Receipt →
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </motion.div>

        {/* Calendly Embed Modal */}
        <AnimatePresence>
          {calendlyUrl && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
              onClick={handleCloseCalendly}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="relative w-full max-w-4xl h-[90vh] neu rounded-2xl overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  onClick={handleCloseCalendly}
                  className="absolute top-4 right-4 z-10 w-10 h-10 neu rounded-full flex items-center justify-center text-text-primary hover:bg-accent/10 transition-colors"
                  aria-label="Close"
                >
                  ✕
                </button>
                {calendlyUrl && (
                  <iframe
                    src={calendlyUrl}
                    width="100%"
                    height="100%"
                    frameBorder="0"
                    title="Book 1-Hour Session"
                    style={{ minHeight: '700px', border: 'none' }}
                    allow="camera; microphone; geolocation"
                    scrolling="no"
                  />
                )}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  )
}

